import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ROLE_IDS, getUserRoles } from "../services/auth";
import { Navigation } from './Navigation';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [userRole, setUserRole] = useState<'interviewer' | 'recruiter' | null>(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, user, getAccessTokenSilently, logout } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingRoles, setIsCheckingRoles] = useState(false);
  const roleCheckTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const roleCheckAttempts = useRef<number>(0);
  const MAX_RETRY_ATTEMPTS = 2;

  // Fetch user's role from Auth0
  const fetchAndSetUserRole = async () => {
    if (!isAuthenticated || !user?.sub || isCheckingRoles) {
      console.log('Layout Step 1: Skipping role check - not authenticated, no user sub, or check in progress');
      setIsLoadingRole(false);
      setIsLoading(false);
      return;
    }

    // Check if we've exceeded max retry attempts
    if (roleCheckAttempts.current >= MAX_RETRY_ATTEMPTS) {
      console.log('Layout: Max retry attempts reached, treating as new user without roles');
      setIsFirstTimeUser(true);
      setUserRole(null);
      setIsLoadingRole(false);
      setIsLoading(false);
      if (location.pathname !== '/onboarding') {
        navigate('/onboarding');
      }
      return;
    }

    try {
      setIsCheckingRoles(true);
      setIsLoadingRole(true);
      console.log('Layout Step 2: Starting role check for user:', user.sub, 'Attempt:', roleCheckAttempts.current + 1);

      // Get user roles - don't force refresh to use cache and avoid hammering the backend
      console.log('Layout Step 3: Fetching roles (no force refresh)');
      const roles = await getUserRoles(user.sub, getAccessTokenSilently, false);
      console.log('Layout Step 3a: Fetched roles:', roles);

      // Reset retry counter on success
      roleCheckAttempts.current = 0;
      
      const hasRoles = roles.length > 0;
      setIsFirstTimeUser(!hasRoles);
      console.log('Layout Step 3b: Has roles:', hasRoles, 'Is first time user:', !hasRoles);
      
      // If this is a first-time user and we're not already on the onboarding page,
      // redirect to the onboarding page
      if (!hasRoles && location.pathname !== '/onboarding') {
        console.log('Layout Step 4: First-time user, redirecting to onboarding');
        navigate('/onboarding');
        setIsLoading(false);
        return;
      }
      
      // Check if user has a role
      const hasInterviewerRole = roles.some(role => role.id === ROLE_IDS.INTERVIEWER);
      const hasRecruiterRole = roles.some(role => role.id === ROLE_IDS.RECRUITER);
      console.log('Layout Step 5: Role check results:', {
        hasInterviewerRole,
        hasRecruiterRole
      });

      if (hasInterviewerRole) {
        console.log('Layout Step 6a: Setting user role to interviewer');
        setUserRole('interviewer');
      } else if (hasRecruiterRole) {
        console.log('Layout Step 6b: Setting user role to recruiter');
        setUserRole('recruiter');
      } else {
        console.log('Layout Step 6c: No role found for user');
        setUserRole(null);
      }

      // If we have a role and we're on the select-role page, redirect to appropriate page
      // But don't redirect away from profile-settings page
      if ((hasInterviewerRole || hasRecruiterRole) && location.pathname === '/select-role') {
        const role = hasRecruiterRole ? 'recruiter' : 'interviewer';
        console.log('Layout Step 7: On select-role page, redirecting to:', role === 'recruiter' ? '/recruiter-dashboard' : '/dashboard');
        navigate(role === 'recruiter' ? '/recruiter-dashboard' : '/dashboard');
        setIsLoading(false);
      } else {
        console.log('Layout Step 7: No redirect needed');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Layout Step 8: Error in fetchAndSetUserRole:', error);

      // Check if this is an Auth0 authentication error (expired/invalid token)
      const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
      const isAuthError =
        errorMessage.includes('refresh token') ||
        errorMessage.includes('invalid token') ||
        errorMessage.includes('token expired') ||
        errorMessage.includes('unauthorized') ||
        (error as any)?.status === 401 ||
        (error as any)?.status === 403;

      if (isAuthError) {
        console.log('Layout: Auth token error detected, logging user out');
        console.error('Layout: Auth error details:', error);

        // Clear state
        setIsFirstTimeUser(false);
        setUserRole(null);
        setIsLoading(false);
        setIsLoadingRole(false);
        roleCheckAttempts.current = 0;

        // Log user out and redirect to landing page
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
        return;
      }

      // For non-auth errors, use retry logic
      roleCheckAttempts.current += 1;

      if (roleCheckAttempts.current >= MAX_RETRY_ATTEMPTS) {
        console.log('Layout: Max retries reached after error, treating as new user');
        setIsFirstTimeUser(true);
        setUserRole(null);
        setIsLoading(false);
        setIsLoadingRole(false);
        if (location.pathname !== '/onboarding') {
          navigate('/onboarding');
        }
      } else {
        console.log('Layout: Will retry role fetch on next navigation/auth change');
        setUserRole(null);
        setIsLoading(false);
        setIsLoadingRole(false);
      }
    } finally {
      setIsCheckingRoles(false);
      setIsLoadingRole(false);
    }
  };

  // Debounced role check
  const debouncedRoleCheck = useCallback(() => {
    if (roleCheckTimeoutRef.current) {
      clearTimeout(roleCheckTimeoutRef.current);
    }

    roleCheckTimeoutRef.current = setTimeout(() => {
      fetchAndSetUserRole();
    }, 1000); // 1 second debounce
  }, [isAuthenticated, user?.sub, location.pathname]);

  // Immediately handle unauthenticated state to avoid showing loading screen to public visitors
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('Layout: User not authenticated, setting loading to false immediately');
      setIsLoading(false);
      setIsLoadingRole(false);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    console.log('Layout: Auth state changed:', { isAuthenticated, userSub: user?.sub });
    if (!authLoading && isAuthenticated) {
      debouncedRoleCheck();
    }

    return () => {
      if (roleCheckTimeoutRef.current) {
        clearTimeout(roleCheckTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user?.sub, authLoading, debouncedRoleCheck]);

  useEffect(() => {
    console.log('Layout: Navigation effect triggered:', {
      userRole,
      location: location.pathname,
      isLoadingRole,
      isAuthenticated,
      isFirstTimeUser
    });

    // If not authenticated or still loading role, don't redirect
    if (!isAuthenticated || isLoadingRole) {
      console.log('Layout: Not authenticated or still loading role, skipping navigation');
      return;
    }
    
    // If this is a first-time user and we're not already on the onboarding or settings page,
    // redirect to the onboarding page
    // Allow /settings and /recruiter/profile so users can complete their profile after selecting a role
    const isAllowedFirstTimeUserPath =
      location.pathname === '/onboarding' ||
      location.pathname === '/settings' ||
      location.pathname === '/get-started' ||
      location.pathname.startsWith('/recruiter/profile') ||
      isPublicPage ||
      location.pathname.startsWith('/legal/');

    if (isFirstTimeUser && !isAllowedFirstTimeUserPath) {
      console.log('Layout: First-time user, redirecting to onboarding', {
        isFirstTimeUser,
        currentPath: location.pathname,
        isAllowed: isAllowedFirstTimeUserPath
      });
      navigate('/onboarding');
      return;
    }

    // If no role is selected and we're not on the role selection page, redirect to it
    // But allow users to stay on settings, onboarding, or recruiter profile pages
    const isAllowedNoRolePath =
      location.pathname === '/select-role' ||
      location.pathname === '/onboarding' ||
      location.pathname === '/settings' ||
      location.pathname.startsWith('/recruiter/profile') ||
      isPublicPage ||
      location.pathname.startsWith('/legal/');

    if (!userRole && !isAllowedNoRolePath) {
      console.log('Layout: No role found, redirecting to select-role', {
        userRole,
        currentPath: location.pathname,
        isAllowedNoRolePath
      });
      navigate('/select-role');
    } else if (userRole && (location.pathname === '/' || location.pathname === '/select-role')) {
      // If role exists and we're on the selection page or root, redirect to appropriate dashboard
      console.log('Layout: Role found, redirecting to appropriate page:', userRole === 'recruiter' ? '/recruiter-dashboard' : '/dashboard');
      navigate(userRole === 'recruiter' ? '/recruiter-dashboard' : '/dashboard');
    }
    // Note: We don't redirect away from /onboarding or /settings even if the user has a role
  }, [userRole, navigate, location, isAuthenticated, isLoadingRole, isFirstTimeUser]);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Loading your profile...</Typography>
      </Box>
    );
  }

  // Check if we're on a public page to conditionally hide navigation
  const publicPageSlugs = [
    'software-engineer-interview-prep',
    'data-scientist-interview-prep',
    'product-manager-interview-prep',
    'consultant-interview-prep',
    'financial-analyst-interview-prep',
    'marketing-manager-interview-prep',
    'coding-interview-practice',
    'case-interview-preparation',
    'technical-interview-simulator',
    'behavioral-interview-coach',
    'system-design-interview-prep',
  ];
  const isPublicPage =
    (location.pathname === '/' && !isAuthenticated) ||
    location.pathname.startsWith('/vs/') ||
    location.pathname.startsWith('/alternatives/') ||
    location.pathname === '/verify-domain' ||
    publicPageSlugs.includes(location.pathname.slice(1));
  const isLandingPage = isPublicPage;

  const { resolvedMode } = useThemeMode();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: isLandingPage ? (resolvedMode === 'dark' ? 'background.default' : 'white') : 'background.default'
    }}>
      {!isLandingPage && <Navigation />}
      <Box component="main" sx={{ flexGrow: 1, p: isLandingPage ? 0 : 3 }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
